import removeRowsFromListViewStore from './removeRowsFromListViewStore';
import findIndexByInstanceKey from '../notifications/helpers/findIndexByInstanceKey';
import findIndexToInsertAt from '../notifications/helpers/findIndexToInsertAt';
import { resetSelection, singleSelectRow } from 'owa-mail-actions';
import TableOperations from 'owa-mail-list-table-operations';
import tombstoneOperations from 'owa-mail-list-tombstone';
import MailListItemSelectionSource from 'owa-mail-store/lib/store/schema/MailListItemSelectionSource';
import { select } from 'satcheljs/lib/legacy';
import { listViewStore, getRowIdsFromRowKeys, getRowKeysFromRowIds, MailRowDataPropertyGetter, } from 'owa-mail-list-store';
/**
 * Add the pin operation to tombstone so the row doesn't pop back into list view
 * @param rowKeys of the rows to be pinned/unpinned
 * @param tableView which contains the rows
 * @param shouldMarkAsPinned whether it is a pin or unpin action
 */
function addPinOperationToTombstone(rowKeys, tableView, shouldMarkAsPinned) {
    // Add row to tombstone, so that the row won't jump around as we get staggered notifications after we pin
    // the item locally.
    var removeFromTombstoneConditionType = shouldMarkAsPinned
        ? 1 /* OnPinNotification */
        : 2 /* OnUnpinNotification */;
    tombstoneOperations.add(rowKeys, tableView.serverFolderId, {
        type: removeFromTombstoneConditionType,
    });
}
/**
 * Move the target row to the index after pin/unpin
 * @param lastDeliveryOrRenewTimeStamp the last delivery or renew time of the target row data
 * @param lastDeliveryTimeStamp the last delivery time of target row data
 * @param tableView the table view
 * @param targetRowKey the target row id
 * @param shouldMarkAsPinned whether this is a pin action, otherwise unpin
 */
function moveUpdatedRowInList(lastDeliveryOrRenewTimeStamp, lastDeliveryTimeStamp, tableView, targetRowKey, shouldMarkAsPinned) {
    var oldIndex = findIndexByInstanceKey(targetRowKey, tableView);
    // Remove row from old position in the list
    if (oldIndex < 0) {
        throw new Error('Row being pinned / unpinnned must exist in the list');
    }
    // Index to start search at. If we're pinning, start from top, if unpinning, it can only be moved
    // to at or below the old index
    var startIndex = shouldMarkAsPinned ? 0 : oldIndex + 1;
    var indexToInsertAt = findIndexToInsertAt(lastDeliveryOrRenewTimeStamp, lastDeliveryTimeStamp, tableView, startIndex);
    if (oldIndex < indexToInsertAt) {
        // In case of unpin, decrease 1 index because indexToInsertAt is calculated based on that row
        // is still pinned in the table
        indexToInsertAt--;
    }
    if (indexToInsertAt == -1) {
        // Remove the row if could not place it, because the row could have moved down (and out of loaded range) for any reason from another client,
        // e.g unpin or delete the latest item in the conversation from another client
        removeRowsFromListViewStore([targetRowKey], tableView);
        return;
    }
    TableOperations.updateRowPosition(indexToInsertAt, targetRowKey, tableView);
}
/**
 * Try select the next row when pin/unpin
 * @param rowKeys the row keys of the rows to pin/unpin
 * @param originalTableView the original table view
 */
function trySelectNextRowUponPinUnpin(rowKeys, originalTableView) {
    var keyOfLastRowToPin = rowKeys[rowKeys.length - 1];
    if (!originalTableView.selectedRowKeys.has(keyOfLastRowToPin)) {
        // Do not update the selection if the pinned row is not selected
        return;
    }
    var indexOfLastRowToPin = originalTableView.rowKeys.indexOf(keyOfLastRowToPin);
    // Select the next row in table if the pinned row is selected and it's not the last row in table
    var shouldSelectNextRow = indexOfLastRowToPin < originalTableView.rowKeys.length - 1;
    if (shouldSelectNextRow) {
        singleSelectRow(originalTableView, originalTableView.rowKeys[indexOfLastRowToPin + 1], false /* isUserNavigation */, MailListItemSelectionSource.RowRemoval);
    }
    else {
        // Clear the selection if there is no next row to select, eg last row in table is selected and pinned
        resetSelection(originalTableView, MailListItemSelectionSource.Reset);
    }
}
/**
 * Mark the row pinned/unpinned in list view store
 * @param rowKeys the rowKeys to act upon
 * @param originalTableViewId the table view id
 * @param shouldMarkAsPinned whether this is a pin action, otherwise unpin
 * @param updateRenewTimeAction the delegate action which updates the row data's renew time
 */
export default select({
    tableViews: function () { return listViewStore.tableViews; },
})(function markRowsPinnedInListViewStore(rowKeys, originalTableViewId, shouldMarkAsPinned, updateRenewTimeAction, state) {
    var originalTableView = state.tableViews.get(originalTableViewId);
    if (originalTableView.tableQuery.type === 0 /* Folder */) {
        // Add the rowKeys to tombstone so they don't pop back in.
        // This needs to be done first as the store update for pin may remove the row from table.
        // Only need to do this for folder table, which supports notification and pin/delete actions
        addPinOperationToTombstone(rowKeys, originalTableView, shouldMarkAsPinned);
        // Select next row.
        // Skip this for other table, like search, as the row being pinned/unpinned never moves in the list
        trySelectNextRowUponPinUnpin(rowKeys, originalTableView);
    }
    var rowIdStrings = getRowIdsFromRowKeys(rowKeys, originalTableViewId);
    rowIdStrings.forEach(function (rowId) {
        state.tableViews.forEach(function (tableView) {
            if (tableView.tableQuery.folderId != originalTableView.tableQuery.folderId) {
                return;
            }
            var rowKeys = getRowKeysFromRowIds([rowId], tableView);
            rowKeys.forEach(function (rowKey) {
                updateRenewTimeAction(rowKey, tableView.id, shouldMarkAsPinned);
                // Only move the pinned row to the top if it's in a mail table.
                // For search table, we don't move the row after it's pinned.
                if (tableView.tableQuery.type === 0 /* Folder */) {
                    moveUpdatedRowInList(MailRowDataPropertyGetter.getLastDeliveryOrRenewTimeStamp(rowKey, tableView), MailRowDataPropertyGetter.getLastDeliveryTimeStamp(rowKey, tableView), tableView, rowKey, shouldMarkAsPinned);
                }
            });
        });
    });
});
//# sourceMappingURL=markRowsPinnedInListViewStore.js.map